/**
 * RUNNER putter upsell.
 *
 * Offer markup is rendered server-side by snippets/putter-upsell.liquid. This
 * file handles: single add-to-cart, the bundle ('add the set') add-to-cart, and
 * the cart-page modal.
 *
 * Listeners are delegated from document: the drawer replaces #MiniCart-<id> and
 * the cart page replaces #MainCart-<id> on every update, either of which would
 * detach a directly bound listener.
 */
(function () {
  'use strict';

  var ADD_SELECTOR = '[data-putter-upsell-add]';
  var BUNDLE_SELECTOR = '[data-putter-bundle-add]';
  var DISMISS_SELECTOR = '[data-putter-upsell-dismiss]';
  var MODAL_SELECTOR = '[data-putter-upsell-modal]';
  var CONSENT_SELECTOR = '.shopify-pc__banner';
  var SEEN_KEY = 'runner_putter_upsell_seen_build';
  var FOCUSABLE = 'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])';

  var lastFocused = null;
  var consentObserver = null;

  function addUrl() {
    return (window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add.js';
  }

  function modal() {
    return document.querySelector(MODAL_SELECTOR);
  }

  function signature() {
    var el = modal();
    return el ? el.getAttribute('data-putter-upsell-signature') || '' : '';
  }

  function seen() {
    try {
      return sessionStorage.getItem(SEEN_KEY) === signature();
    } catch (e) {
      return true;
    }
  }

  function markSeen() {
    try {
      sessionStorage.setItem(SEEN_KEY, signature());
    } catch (e) {
      /* no-op */
    }
  }

  function consentBannerVisible() {
    var banner = document.querySelector(CONSENT_SELECTOR);
    if (!banner) return false;
    var cs = getComputedStyle(banner);
    return cs.display !== 'none' && cs.visibility !== 'hidden' && banner.offsetHeight > 0;
  }

  function trapFocus(event) {
    var el = modal();
    if (!el || el.hasAttribute('hidden') || event.key !== 'Tab') return;
    var items = Array.prototype.filter.call(
      el.querySelectorAll(FOCUSABLE),
      function (n) { return n.offsetParent !== null; }
    );
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openModal() {
    var el = modal();
    if (!el || seen()) return;
    if (el.parentElement !== document.body) document.body.appendChild(el);
    lastFocused = document.activeElement;
    el.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    var panel = el.querySelector('.putter-upsell-modal__panel');
    if (panel) panel.focus();
    document.addEventListener('keydown', trapFocus, true);
  }

  function closeModal() {
    var el = modal();
    if (!el) return;
    el.setAttribute('hidden', '');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', trapFocus, true);
    markSeen();
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }

  function scheduleOpen() {
    if (!modal() || seen()) return;
    if (!consentBannerVisible()) {
      openModal();
      return;
    }
    if (consentObserver) consentObserver.disconnect();
    consentObserver = new MutationObserver(function () {
      if (seen()) { consentObserver.disconnect(); return; }
      if (!consentBannerVisible()) {
        consentObserver.disconnect();
        openModal();
      }
    });
    consentObserver.observe(document.body, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['style', 'class', 'hidden']
    });
  }

  function refreshCart() {
    if (document.querySelector('[id^="MiniCart-"]')) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
    } else {
      markSeen();
      window.location.reload();
    }
  }

  function postAdd(payload, onError) {
    return fetch(addUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        if (!response.ok) {
          return response.text().then(function (text) { throw new Error(text); });
        }
        return response.json();
      })
      .then(refreshCart)
      .catch(function (error) {
        console.error('[putter-upsell] add failed', error);
        if (onError) onError();
      });
  }

  function addSingle(button) {
    button.setAttribute('aria-busy', 'true');
    postAdd(
      { items: [{ id: Number(button.getAttribute('data-putter-upsell-add')), quantity: 1 }] },
      function () { button.removeAttribute('aria-busy'); }
    );
  }

  /**
   * The bundle is a single priced line; the four pieces ride along as line-item
   * properties so fulfilment knows what to pack. Rather than duplicate the
   * not-chosen logic here, read the four offers already rendered in the same
   * container -- the snippet only shows the 'Add the set' button when exactly
   * two faces and two weights are present.
   */
  function addBundle(button) {
    var container = button.closest('.putter-upsell, .putter-upsell-modal__panel') || document;
    var offers = Array.prototype.slice.call(container.querySelectorAll(ADD_SELECTOR));
    var props = {};
    var faceN = 0, weightN = 0;

    offers.forEach(function (o) {
      var li = o.closest('li');
      if (!li) return;
      var title = ((li.querySelector('.putter-upsell__title') || {}).textContent || '').trim();
      var variant = ((li.querySelector('.text-opacity') || {}).textContent || '').trim();
      var id = o.getAttribute('data-putter-upsell-add');
      if (/face/i.test(title)) {
        faceN += 1;
        props['Face ' + faceN] = variant;
        props['_face_' + faceN + '_id'] = id;
      } else {
        weightN += 1;
        props['Weight ' + weightN] = variant;
        props['_weight_' + weightN + '_id'] = id;
      }
    });

    var ref = button.getAttribute('data-putter-bundle-ref');
    if (ref) props['_completes_build'] = ref;

    button.setAttribute('aria-busy', 'true');
    postAdd(
      { items: [{ id: Number(button.getAttribute('data-putter-bundle-add')), quantity: 1, properties: props }] },
      function () { button.removeAttribute('aria-busy'); }
    );
  }

  document.addEventListener('click', function (event) {
    if (event.target.closest(DISMISS_SELECTOR)) {
      event.preventDefault();
      closeModal();
      return;
    }

    var bundleBtn = event.target.closest(BUNDLE_SELECTOR);
    if (bundleBtn) {
      event.preventDefault();
      if (!bundleBtn.hasAttribute('aria-busy')) addBundle(bundleBtn);
      return;
    }

    var button = event.target.closest(ADD_SELECTOR);
    if (!button || button.hasAttribute('aria-busy')) return;
    event.preventDefault();
    addSingle(button);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeModal();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleOpen);
  } else {
    scheduleOpen();
  }
})();
